/**
 * @fileoverview Filter Summary Component
 * 
 * This component displays a summary of active filters with options to remove
 * individual filters or clear all filters.
 * 
 * Requirements: 3.3, 3.4
 */

import React from 'react';

/**
 * Filter Summary Component
 * 
 * @param {Object} props - Component props
 * @param {ActiveFilter[]} props.activeFilters - Currently active filters
 * @param {function(string): void} props.onFilterRemove - Handler for removing individual filters
 * @param {function(): void} props.onClearAll - Handler for clearing all filters
 * @param {string} props.testId - Test ID
 */
export const FilterSummary = ({
  activeFilters,
  onFilterRemove,
  onClearAll,
  testId = 'filter-summary'
}) => {
  if (!activeFilters || activeFilters.length === 0) {
    return null;
  }

  /**
   * Format filter value for display
   */
  const formatFilterValue = (filter) => {
    if (Array.isArray(filter.value)) {
      if (filter.operator?.type === 'between') {
        return `${filter.value[0]} - ${filter.value[1]}`;
      }
      return filter.value.join(', ');
    }
    
    if (typeof filter.value === 'boolean') {
      return filter.value ? 'Yes' : 'No';
    }
    
    if (filter.value instanceof Date) {
      return filter.value.toLocaleDateString();
    }
    
    return String(filter.value);
  };

  /**
   * Get display text for filter
   */
  const getFilterDisplayText = (filter) => {
    const operatorLabel = filter.operator?.label || '';
    const value = formatFilterValue(filter);
    
    // For simple operators like "equals", we can omit the operator label
    if (filter.operator?.type === 'equals') {
      return value;
    }
    
    return `${operatorLabel} ${value}`;
  };

  return (
    <div className="filter-summary" data-testid={testId}>
      <div className="filter-summary-header">
        <span className="filter-summary-title">
          Active Filters ({activeFilters.length})
        </span>
        
        {onClearAll && (
          <button
            className="filter-clear-all-button"
            onClick={onClearAll}
            title="Clear all filters"
            data-testid={`${testId}-clear-all`}
          >
            Clear All
          </button>
        )}
      </div>

      <div className="filter-summary-tags">
        {activeFilters.map((filter) => (
          <div
            key={filter.id}
            className="filter-tag"
            data-testid={`${testId}-tag-${filter.id}`}
          >
            <span className="filter-tag-label">
              {filter.id}:
            </span>
            <span className="filter-tag-value">
              {getFilterDisplayText(filter)}
            </span>
            
            {onFilterRemove && (
              <button
                className="filter-tag-remove"
                onClick={() => onFilterRemove(filter.id)}
                title={`Remove ${filter.id} filter`}
                data-testid={`${testId}-remove-${filter.id}`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
